import React, { useState } from "react";
import HeaderInfo from "../components/common/HeaderInfo";
import DataTable from "../components/common/DataTable";
import { useGetNotificationsQuery, useMarkNotificationAsReadMutation } from "../store/api";
import { CheckCircle2, Clock, Mail } from "lucide-react";
import toast from "react-hot-toast";

export default function Notifications() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  
  const { data, isLoading, isFetching, refetch } = useGetNotificationsQuery({
    page,
    limit,
  });

  const [markAsRead, { isLoading: isMarking }] = useMarkNotificationAsReadMutation();

  const notifications = data?.data?.notifications || [];
  const totalPages = data?.data?.pagination?.pages || 1;

  const handleNext = () => setPage((p) => Math.min(totalPages, p + 1));
  const handlePrev = () => setPage((p) => Math.max(1, p - 1));

  const handleMarkAsRead = async (id) => {
    try {
      await markAsRead(id).unwrap();
      refetch();
    } catch (error) {
      toast.error("Failed to mark as read");
    }
  };

  const columns = [
    { key: "statusIcon", label: "", className: "col-span-1 text-center" },
    { key: "details", label: "Notification Details", className: "col-span-7" },
    { key: "time", label: "Time", className: "col-span-2 text-center" },
    { key: "action", label: "Action", className: "col-span-2 text-right" },
  ];

  const renderRow = (r, idx) => {
    const isUnread = r.status === "unread";
    return (
      <div
        key={r.id}
        className={`transition-colors border-b border-[#E5E5E5] ${
          isUnread ? "bg-[#f4f0f8]" : "bg-[#EEEEEE40]"
        } ${idx === notifications.length - 1 ? "pb-4" : ""}`}
      >
        <div className="grid grid-cols-12 gap-4 px-4 sm:px-7 py-6 items-center">
          <div className="col-span-1 flex justify-center">
            {isUnread ? (
              <div className="w-10 h-10 rounded-full bg-[#FFE2E2] flex items-center justify-center text-red-500 shadow-sm">
                <Mail size={18} />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-full bg-[#D8FFDF] flex items-center justify-center text-green-600 shadow-sm">
                <CheckCircle2 size={18} />
              </div>
            )}
          </div>

          <div className="col-span-7">
            <h4 className={`text-base sm:text-lg ${isUnread ? "font-bold text-black" : "font-medium text-gray-700"}`}>
              {r.title}
            </h4>
            <p className={`text-sm mt-1 leading-5 ${isUnread ? "text-gray-800" : "text-[#878686]"}`}>
              {r.message}
            </p>
          </div>

          <div className="col-span-2 text-center flex flex-col items-center justify-center">
            <Clock size={16} className="text-gray-400 mb-1" />
            <span className="text-xs text-[#878686] font-medium">
              {new Date(r.createdAt).toLocaleString()}
            </span>
          </div>

          <div className="col-span-2 flex justify-end gap-3 pr-2">
            {isUnread ? (
              <button
                onClick={() => handleMarkAsRead(r.id)}
                disabled={isMarking}
                className="text-xs sm:text-sm bg-white border border-[#6D4E8A] text-[#6D4E8A] font-medium px-4 py-2 rounded-lg hover:bg-[#6D4E8A] hover:text-white transition-colors disabled:opacity-50"
              >
                Mark Read
              </button>
            ) : (
              <span className="text-xs sm:text-sm text-green-600 font-medium px-4 py-2 bg-green-50 rounded-lg border border-green-100">
                Read
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="pt-6 md:pt-14 px-4 md:pl-8 md:pr-12 min-h-screen">
      <HeaderInfo
        title="All Notifications"
        desc="Review all your system alerts and recent activity"
        showFilterButton={false}
        btnLabel="Mark All Read"
        btnIcon={false}
        onAdd={() => handleMarkAsRead()}
      />
      <div className="mt-6 ml-0 md:ml-2.5">
        <DataTable
          columns={columns}
          rows={notifications}
          renderRow={renderRow}
          page={page}
          totalPages={totalPages}
          onNext={handleNext}
          onPrev={handlePrev}
          isLoading={isLoading || isFetching}
        />
      </div>
    </div>
  );
}
